import { db, functions } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const materialsCollection = collection(db, "materials");
const MAX_MATERIAL_SIZE = 20 * 1024 * 1024;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const uploadMaterial = async ({
  file,
  title,
  tags = [],
  skills = [],
  audience = "student",
  uploadedBy,
}) => {
  try {
    if (!file) throw new Error("No file provided");
    if (!uploadedBy?.id) throw new Error("User is required to upload material.");
    if (file.size > MAX_MATERIAL_SIZE) {
      throw new Error("File is too large. Please upload a file up to 20 MB.");
    }

    const fileBase64 = await fileToBase64(file);
    const uploadMaterialFn = httpsCallable(functions, "uploadMaterial");
    const response = await uploadMaterialFn({
      userId: uploadedBy.id,
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      fileBase64,
      title,
      tags,
      skills,
      audience,
    });

    return response.data;
  } catch (error) {
    console.error("Error uploading material:", error);
    if (error.code === "functions/permission-denied") {
      throw new Error(
        error.message || "Only admins can upload materials."
      );
    }
    if (
      error.code === "functions/not-found" ||
      error.code === "functions/unavailable"
    ) {
      throw new Error(
        "Material upload service is not available. Please deploy Firebase Functions and try again."
      );
    }
    if (error.code?.startsWith("functions/")) {
      throw new Error(error.message || "Material upload failed.");
    }
    throw error;
  }
};

export const getMaterials = async () => {
  try {
    const q = query(materialsCollection, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error("Error fetching materials:", error);
    return [];
  }
};

export const materialMatchesAudience = (material, audience) => {
  if (!audience || audience === "all") return true;

  const materialAudience = material.audience || "student";
  return materialAudience === audience || materialAudience === "all";
};
